import { useCallback, useEffect, useRef, useState } from 'react';
import { AGRITOUR_DRAFT_COPY } from './agritour-form-copy';
import styles from './AgriTourScreen.module.css';

type AgriTourDraftModalProps = {
  resumeUrl: string;
  expiresAt: string;
  onClose: () => void;
};

export function AgriTourDraftModal({ resumeUrl, expiresAt, onClose }: AgriTourDraftModalProps) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const dialog = dialogRef.current;
    dialog?.showModal();
    return () => {
      dialog?.close();
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(resumeUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      const input = document.createElement('textarea');
      input.value = resumeUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      input.remove();
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    }
  }, [resumeUrl]);

  const expiryLabel = new Date(expiresAt).toLocaleDateString('he-IL');

  return (
    <dialog
      ref={dialogRef}
      className={styles.draftModal}
      aria-labelledby="agritour-draft-title"
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        if (event.target === dialogRef.current) {
          onClose();
        }
      }}
    >
      <div className={styles.draftModalPanel}>
        <button
          type="button"
          className={styles.draftModalClose}
          onClick={onClose}
          aria-label={AGRITOUR_DRAFT_COPY.closeButton}
        >
          ×
        </button>
        <h2 id="agritour-draft-title" className={styles.draftModalTitle}>
          {AGRITOUR_DRAFT_COPY.modalTitle}
        </h2>
        <p className={styles.draftModalBody}>{AGRITOUR_DRAFT_COPY.modalBody}</p>
        <div className={styles.draftModalLinkRow}>
          <input
            className={styles.draftModalInput}
            type="text"
            readOnly
            value={resumeUrl}
            dir="ltr"
            aria-label="קישור לטיוטה"
            onFocus={(event) => event.currentTarget.select()}
          />
          <button type="button" className={styles.btnSecondary} onClick={() => void handleCopy()}>
            {copied ? AGRITOUR_DRAFT_COPY.copiedButton : AGRITOUR_DRAFT_COPY.copyButton}
          </button>
        </div>
        <p className={styles.draftModalDisclaimer}>{AGRITOUR_DRAFT_COPY.disclaimer}</p>
        <p className={styles.draftModalExpiry}>
          {AGRITOUR_DRAFT_COPY.expiryNote}
          {expiryLabel ? ` (עד ${expiryLabel})` : ''}
        </p>
        <button type="button" className={styles.btnPrimary} onClick={onClose}>
          {AGRITOUR_DRAFT_COPY.closeButton}
        </button>
      </div>
    </dialog>
  );
}
